/**
 * Report charts, built on the app's shadcn chart wrapper (components/ui/chart)
 * over recharts — both shipped with the app and previously unused.
 *
 * The rules from .interface-design/system.md still apply:
 *  - counts and dollars never share an axis;
 *  - the invoice-stage ramp (gray/amber/emerald) and the bucket palette
 *    (indigo/teal/violet) share no colour;
 *  - parts of a stack must be mutually exclusive and sum to the whole.
 */
import {
  Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis,
} from "recharts";
import {
  ChartContainer, ChartTooltip, ChartTooltipContent,
  ChartLegend, ChartLegendContent, type ChartConfig,
} from "@/components/ui/chart";

export const STAGE_CHART_CONFIG = {
  notInvoiced: { label: "Not invoiced", color: "#9ca3af" },     // gray-400
  outstanding: { label: "Awaiting payment", color: "#f59e0b" }, // amber-500
  collected:   { label: "Collected to date", color: "#047857" },// emerald-700
} satisfies ChartConfig;

const usd = (v: number) => "$" + Math.round(v).toLocaleString("en-US");

/**
 * Horizontal stacked bars, one per bucket, segmented by invoice stage.
 * Dollar-denominated; the shared x-axis is what encodes magnitude.
 */
export function BucketStageChart({
  rows,
}: { rows: { bucketLabel: string; notInvoicedCents: number; outstandingCents: number; collectedCents: number }[] }) {
  const data = rows.map(r => ({
    bucket: r.bucketLabel,
    notInvoiced: r.notInvoicedCents / 100,
    outstanding: r.outstandingCents / 100,
    collected: r.collectedCents / 100,
  }));
  return (
    <ChartContainer config={STAGE_CHART_CONFIG} className="w-full" style={{ height: 40 + data.length * 56 }}>
      <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
        <CartesianGrid horizontal={false} strokeDasharray="3 3" />
        <XAxis type="number" tickFormatter={usd} fontSize={11} stroke="#6b7280" />
        <YAxis type="category" dataKey="bucket" width={150} fontSize={12} stroke="#6b7280"
               tickLine={false} axisLine={false} />
        <ChartTooltip content={<ChartTooltipContent formatter={(v, name) =>
          [`${usd(Number(v))} `, STAGE_CHART_CONFIG[name as keyof typeof STAGE_CHART_CONFIG]?.label ?? name]} />} />
        <ChartLegend content={<ChartLegendContent />} />
        <Bar dataKey="notInvoiced" stackId="s" fill="var(--color-notInvoiced)" radius={[3, 0, 0, 3]} />
        <Bar dataKey="outstanding" stackId="s" fill="var(--color-outstanding)" />
        <Bar dataKey="collected" stackId="s" fill="var(--color-collected)" radius={[0, 3, 3, 0]} />
      </BarChart>
    </ChartContainer>
  );
}

/**
 * Horizontal count bars — ranked categories (age bands, exceptions, owners).
 * Deliberately single-series: counts never share an axis with dollars.
 */
export function CountBarChart({
  rows, label = "Count", color = "#4f46e5", height,
}: { rows: { label: string; value: number }[]; label?: string; color?: string; height?: number }) {
  if (rows.length === 0) return <p className="text-sm text-gray-500 py-4">Nothing to show.</p>;
  const config = { value: { label, color } } satisfies ChartConfig;
  return (
    <ChartContainer config={config} className="w-full"
                    style={{ height: height ?? Math.max(120, 36 + rows.length * 40) }}>
      <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 24 }}>
        <CartesianGrid horizontal={false} strokeDasharray="3 3" />
        <XAxis type="number" allowDecimals={false} fontSize={11} stroke="#6b7280" />
        <YAxis type="category" dataKey="label" width={210} fontSize={12} stroke="#6b7280"
               tickLine={false} axisLine={false} />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Bar dataKey="value" fill="var(--color-value)" radius={3} />
      </BarChart>
    </ChartContainer>
  );
}

/**
 * Percentage bars on a FIXED 0-100 axis — shadcn's custom-label bar pattern.
 * Normalising a share against the largest share renders 9% as a full bar and
 * implies 100%; the fixed domain is the point of this component.
 */
export function PercentBarChart({
  rows, label = "Share", color = "#047857",
}: { rows: { label: string; value: number; sub?: string }[]; label?: string; color?: string }) {
  if (rows.length === 0) return <p className="text-sm text-gray-500 py-4">Nothing to show.</p>;
  const config = { value: { label, color } } satisfies ChartConfig;
  return (
    <ChartContainer config={config} className="w-full"
                    style={{ height: Math.max(120, 36 + rows.length * 44) }}>
      <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 40 }}>
        <CartesianGrid horizontal={false} strokeDasharray="3 3" />
        <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} fontSize={11} stroke="#6b7280" />
        <YAxis type="category" dataKey="label" width={190} fontSize={12} stroke="#6b7280"
               tickLine={false} axisLine={false} />
        <ChartTooltip content={<ChartTooltipContent
          formatter={(v, _n, item) => [`${Number(v).toFixed(0)}% `,
            item?.payload?.sub ?? label]} />} />
        <Bar dataKey="value" fill="var(--color-value)" radius={3} background={{ fill: "#f3f4f6", radius: 3 }}>
          <LabelList dataKey="value" position="right" formatter={(v: number) => `${Math.round(v)}%`}
                     className="fill-gray-700" fontSize={11} />
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}
